// Access-key management for a group's owner, used by Groups.vue.
//
// A groupAccess row (id = the haikuboxGroups id) is what makes a group
// private. With no row, the group is open. The groups API only honours a row
// whose owner matches the group's owner.
//
// The GraphQL is written out here so this works before `amplify codegen`
// regenerates src/graphql. These calls use the app's default auth mode (the
// signed-in user), which the owner rule on groupAccess checks.

import { API } from "aws-amplify";

const getGroupAccessQuery = /* GraphQL */ `
  query GetGroupAccess($id: ID!) {
    getGroupAccess(id: $id) {
      id
      owner
      gkey
      updatedAt
    }
  }
`;

const createGroupAccessMutation = /* GraphQL */ `
  mutation CreateGroupAccess($input: CreateGroupAccessInput!) {
    createGroupAccess(input: $input) {
      id
      gkey
      updatedAt
    }
  }
`;

const updateGroupAccessMutation = /* GraphQL */ `
  mutation UpdateGroupAccess($input: UpdateGroupAccessInput!) {
    updateGroupAccess(input: $input) {
      id
      gkey
      updatedAt
    }
  }
`;

const deleteGroupAccessMutation = /* GraphQL */ `
  mutation DeleteGroupAccess($input: DeleteGroupAccessInput!) {
    deleteGroupAccess(input: $input) {
      id
    }
  }
`;

// 24 random bytes as URL-safe base64: 32 characters, fine in a share link.
export function generateKey() {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

// The group's current key, or null if the group is open.
export async function getGroupKey(groupId) {
  const result = await API.graphql({ query: getGroupAccessQuery, variables: { id: groupId } });
  const row = result.data.getGroupAccess;
  return row ? row.gkey || null : null;
}

// Give the group a new random key, creating its row if needed. Returns the
// key. Links carrying the previous key stop working immediately.
export async function setNewGroupKey(groupId, owner) {
  const gkey = generateKey();
  const existing = await API.graphql({ query: getGroupAccessQuery, variables: { id: groupId } });
  if (existing.data.getGroupAccess) {
    await API.graphql({ query: updateGroupAccessMutation, variables: { input: { id: groupId, gkey } } });
  } else {
    await API.graphql({ query: createGroupAccessMutation, variables: { input: { id: groupId, owner, gkey } } });
  }
  return gkey;
}

// Make the group open again.
export async function removeGroupKey(groupId) {
  await API.graphql({ query: deleteGroupAccessMutation, variables: { input: { id: groupId } } });
}

// A link that opens the group page already unlocked. The key sits after the
// '#', so it's never sent to any server; the dashboard stores it and removes
// it from the address bar.
export function shareLink(slug, key) {
  return `${window.location.origin}/#/groups/${encodeURIComponent(slug)}?key=${encodeURIComponent(key)}`;
}
